import type { EventResponseData } from '../lib/api-client'
import type { Sportsbook } from '@/types/types'
import { useState } from 'react'
import Bet365Logo from '../assets/bet365.svg?react'
import BetMGMLogo from '../assets/betmgm.svg?react'
import DraftKingsLogo from '../assets/draftkings.svg?react'

import FanDuelLogo from '../assets/fanduel.svg?react'
import NXTbetsLogo from '../assets/nxtbets.svg?react'

type SelectionHandler = (
  marketIndex: number,
  selectionIndex: number,
  sportsbook: Sportsbook
) => void

const sportsbookConfig = {
  FanDuel: { color: 'bg-fanduel-secondary', logo: FanDuelLogo },
  Bet365: { color: 'bg-bet365-secondary', logo: Bet365Logo },
  DraftKings: { color: 'bg-draftkings-secondary', logo: DraftKingsLogo },
  BetMGM: { color: 'bg-betmgm-secondary', logo: BetMGMLogo },
}

const availableSportsbooks = [
  'FanDuel',
  'BetMGM',
  'Bet365',
  'DraftKings',
] as const

function SportsbookCheckbox({
  sportsbook,
  isSelected,
  onChange,
}: {
  sportsbook: Sportsbook
  isSelected: boolean
  onChange: (sportsbook: Sportsbook, checked: boolean) => void
}) {
  const config = sportsbookConfig[sportsbook]
  const LogoComponent = config.logo

  return (
    <button
      type="button"
      onClick={() => onChange(sportsbook, !isSelected)}
      className={`
        relative flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all duration-200 
        hover:scale-105 active:scale-95 cursor-pointer min-w-0 flex-shrink-0
        ${isSelected
      ? 'border-[#0db5ff] bg-blue-50 shadow-md'
      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
    }
      `}
      aria-label={`${isSelected ? 'Unselect' : 'Select'} ${sportsbook}`}
    >
      {/* Custom checkbox indicator */}
      <div className={`
        w-4 h-4 rounded border-2 flex items-center justify-center transition-colors duration-200
        ${isSelected
      ? 'bg-[#0db5ff] border-[#0db5ff]'
      : 'bg-white border-gray-300'
    }
      `}
      >
        {isSelected && (
          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </div>

      {/* Logo */}
      <LogoComponent className="size-6 flex-shrink-0" />

    </button>
  )
}

function SelectionRow({
  player,
  marketIndex,
  playerIndex,
  oddsFormat,
  selectedSportsbooks,
  onSelectionClick,
}: {
  player: EventResponseData[number]['selections'][number]
  marketIndex: number
  playerIndex: number
  oddsFormat: 'american' | 'decimal'
  selectedSportsbooks: Set<Sportsbook>
  onSelectionClick: SelectionHandler
}) {
  const filteredSportsbooks = availableSportsbooks
    .filter((sportsbook) => {
      const odds = player.odds[sportsbook]
      return odds && selectedSportsbooks.has(sportsbook)
    })

  return (
    <div className="px-2 @md:pl-6 @md:pr-2 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="font-bold text-gray-900 text-sm @md:text-base">
            {player.name}
          </div>
        </div>
        <div className="flex gap-1">
          {filteredSportsbooks.length > 0
            ? filteredSportsbooks.map((sportsbook) => {
                const config = sportsbookConfig[sportsbook]
                const odds = player.odds[sportsbook]
                const oddsValue = oddsFormat === 'american' ? odds!.american : odds!.decimal
                const LogoComponent = config.logo

                return (
                  <button
                    key={sportsbook}
                    onClick={() => onSelectionClick(marketIndex, playerIndex, sportsbook)}
                    aria-label={`Place bet with ${sportsbook} at ${oddsValue} odds`}
                    className={`${config.color} overflow-hidden rounded text-sm font-medium flex flex-col items-center gap-1 pb-1 @md:flex-row @md:p-0 @md:pr-3.5 @md:gap-4 transition-all duration-150 hover:opacity-80 active:scale-95 cursor-pointer`}
                    type="button"
                  >
                    <LogoComponent className="size-12 md:size-10" />
                    <span className="font-bold text-[10px] @md:text-base @md:w-16">
                      {oddsValue}
                    </span>
                  </button>
                )
              })
            : (
                <div className="h-[70px] @md:h-10 flex items-center">
                  <span className="text-sm text-gray-400 italic">No odds available</span>
                </div>
              )}
        </div>
      </div>
    </div>
  )
}

const H2H_SENTINEL = -1

function Widget({
  leagueName,
  markets,
  selectedMarketIndex,
  onMarketChange,
  oddsFormat,
  onToggleOddsFormat,
  onSelectionClick,
  isH2HMode = false,
  h2hIndices,
  eventName,
}: {
  leagueName: string
  markets: EventResponseData
  selectedMarketIndex: number
  onMarketChange: (marketIndex: number) => void
  oddsFormat: 'american' | 'decimal'
  onToggleOddsFormat: () => void
  onSelectionClick: SelectionHandler
  isH2HMode?: boolean
  h2hIndices?: number[]
  eventName?: string
}) {
  // Initialize with all sportsbooks selected
  const [selectedSportsbooks, setSelectedSportsbooks] = useState<Set<Sportsbook>>(
    () => new Set(availableSportsbooks),
  )

  const isShowingH2H = selectedMarketIndex === H2H_SENTINEL
  const widgetData = isShowingH2H ? undefined : markets[selectedMarketIndex]
  const h2hMarkets = h2hIndices ? h2hIndices.map(i => ({ market: markets[i]!, index: i })) : []

  const handleOddsFormatChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newFormat = event.target.value as 'american' | 'decimal'
    if (newFormat !== oddsFormat) {
      onToggleOddsFormat()
    }
  }

  const handleMarketChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newMarketIndex = Number.parseInt(event.target.value)
    onMarketChange(newMarketIndex)
  }

  const handleSportsbookChange = (sportsbook: Sportsbook, checked: boolean) => {
    setSelectedSportsbooks((prev) => {
      const newSet = new Set(prev)
      if (checked) {
        newSet.add(sportsbook)
      }
      else {
        newSet.delete(sportsbook)
      }
      return newSet
    })
  }

  // Check if any sportsbooks are selected
  const hasSelectedSportsbooks = selectedSportsbooks.size > 0

  return (
    <div className="@container bg-white rounded-lg shadow-xl overflow-hidden mx-auto border-[3px] border-[#C8E2FF] h-[500px] md:h-[700px] flex flex-col" style={{ maxWidth: '1240px' }}>
      {/* Fixed Header */}
      <div className="border-b border-gray-200 pt-2.5 px-4 @md:pt-7 @md:px-7 flex-shrink-0">
        <div className="flex flex-col gap-3 @min-[720px]:flex-row @min-[720px]:items-center @min-[720px]:justify-between @min-[720px]:gap-4">
          <div className="text-center @md:text-left">
            <h2 className={(isH2HMode && !eventName) ? 'text-xl font-bold' : 'text-md text-gray-500'}>
              {leagueName}
            </h2>
            {!isH2HMode && !isShowingH2H && widgetData && (
              <div className="text-xl font-bold mt-0.5">
                {widgetData.name}
              </div>
            )}
            {(isH2HMode || isShowingH2H) && eventName && (
              <div className="text-xl font-bold mt-0.5">
                {eventName}
              </div>
            )}
          </div>
          {!isH2HMode && (
            <select
              value={selectedMarketIndex}
              onChange={handleMarketChange}
              className="px-3 py-1 text-sm font-medium bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0db5ff] focus:border-transparent @md:text-base"
            >
              {markets.map((market, index) => {
                // Skip individual H2H markets — they're grouped under "Head to Head"
                if (h2hIndices?.includes(index))
                  return null
                return (
                  <option key={`${market.name}-${market.marketName}`} value={index}>
                    {market.name !== widgetData?.name ? `${market.name} - ${market.marketName}` : market.marketName}
                  </option>
                )
              })}
              {h2hIndices && h2hIndices.length > 0 && (
                <option value={H2H_SENTINEL}>Head to Head</option>
              )}
            </select>
          )}
        </div>

        {/* Sportsbook Filter */}
        <div className="mt-3">
          <div className="flex items-center gap-2 @md:gap-3 flex-wrap justify-center @md:justify-start">
            {availableSportsbooks.map(sportsbook => (
              <SportsbookCheckbox
                key={sportsbook}
                sportsbook={sportsbook}
                isSelected={selectedSportsbooks.has(sportsbook)}
                onChange={handleSportsbookChange}
              />
            ))}
          </div>
        </div>

        <div className="h-3 @md:h-5" />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4">
        {!hasSelectedSportsbooks
          ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <p className="text-lg font-medium">No sportsbooks selected</p>
                  <p className="text-sm">Please select at least one sportsbook to view odds</p>
                </div>
              </div>
            )
          : (isH2HMode || isShowingH2H)
            ? (
                <div className="divide-y-1 divide-gray-200">
                  {(isH2HMode ? markets.map((m, i) => ({ market: m, index: i })) : h2hMarkets).map(({ market, index: marketIndex }) => (
                    <div key={`${market.name}-${market.marketName}`} className="py-3">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 @md:px-6 mb-2">
                        {market.name}
                      </div>
                      {market.selections.map((player, playerIndex) => (
                        <SelectionRow
                          key={player.name}
                          player={player}
                          marketIndex={marketIndex}
                          playerIndex={playerIndex}
                          oddsFormat={oddsFormat}
                          selectedSportsbooks={selectedSportsbooks}
                          onSelectionClick={onSelectionClick}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              )
            : (
                <div className="divide-y-1 divide-gray-200">
                  {(widgetData?.selections || []).map((player, playerIndex) => (
                    <SelectionRow
                      key={player.name}
                      player={player}
                      marketIndex={selectedMarketIndex}
                      playerIndex={playerIndex}
                      oddsFormat={oddsFormat}
                      selectedSportsbooks={selectedSportsbooks}
                      onSelectionClick={onSelectionClick}
                    />
                  ))}
                </div>
              )}
      </div>

      <div className="p-4 flex-shrink-0 border-t border-gray-200 flex items-center justify-between @md:px-6">
        <div className="flex items-center gap-1.5">
          <span className="text-[8px] @md:text-xs text-gray-400">Powered by</span>
          <NXTbetsLogo className="h-[16px] w-auto" />
        </div>
        <select
          value={oddsFormat}
          onChange={handleOddsFormatChange}
          className="px-2 py-1 text-[8px] @md:text-xs font-medium bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent @md:px-3"
        >
          <option value="american">American</option>
          <option value="decimal">Decimal</option>
        </select>
      </div>
    </div>
  )
}

export default Widget
